import React, { useState, useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext';

// Password validation configuration
const PASSWORD_RULES = {
  minLength: 8,
  requireLetters: true,
  requireNumbers: true,
  requireMix: true,
};

interface PasswordStrength {
  isValid: boolean;
  strength: 'weak' | 'ok' | 'strong';
  message: string;
}

function validatePasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return { isValid: false, strength: 'weak', message: 'Password is required' };
  }
  
  if (password.length < PASSWORD_RULES.minLength) {
    return { 
      isValid: false, 
      strength: 'weak', 
      message: `Password must be at least ${PASSWORD_RULES.minLength} characters` 
    };
  }

  const hasLetters = /[a-zA-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[^a-zA-Z0-9]/.test(password);
  
  if (PASSWORD_RULES.requireLetters && !hasLetters) {
    return { 
      isValid: false, 
      strength: 'weak', 
      message: 'Password must contain letters' 
    };
  }
  
  if (PASSWORD_RULES.requireNumbers && !hasNumbers) {
    return { 
      isValid: false, 
      strength: 'weak', 
      message: 'Password must contain numbers' 
    };
  }

  if (PASSWORD_RULES.requireMix && !(hasLetters && hasNumbers)) {
    return { 
      isValid: false, 
      strength: 'weak', 
      message: 'Password must contain both letters and numbers' 
    };
  }

  // Determine strength
  if (password.length >= 12 && hasLetters && hasNumbers && hasSpecialChars) {
    return { isValid: true, strength: 'strong', message: 'Strong password' };
  }
  
  return { isValid: true, strength: 'ok', message: 'Password meets minimum requirements' };
}

export const ChangePasswordScreen: React.FC = () => {
  const { changePassword, logout, loading, error } = useAuth();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [touched, setTouched] = useState({ old: false, new: false, confirm: false });

  // Validate password strength
  const passwordStrength = useMemo(() => validatePasswordStrength(newPwd), [newPwd]);
  
  // Check if passwords match
  const passwordsMatch = newPwd && confirmPwd && newPwd === confirmPwd;
  const showMatchError = touched.confirm && confirmPwd && !passwordsMatch;
  
  // Form is valid
  const isFormValid = oldPwd && passwordStrength.isValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    
    try {
      await changePassword(oldPwd, newPwd);
      // Success - AuthContext will automatically clear needsPasswordChange and navigate to MainApp
    } catch (err) {
      // Error handled by AuthContext
    }
  };

  const handleSignOut = async () => {
    await logout();
  };

  return (
    <main className="card form-card" role="main" aria-labelledby="changePasswordTitle">
      <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
      <span className="tag" style={{ background: 'linear-gradient(180deg, #fff4e6, #ffe8cc)', color: '#7a4500', borderColor: 'rgba(255,165,0,0.3)' }}>
        Action Required
      </span>
      <h1 id="changePasswordTitle">Update your password to continue</h1>
      <p className="desc">
        Your password has been reset by an administrator and you must choose a new one before continuing to access your account.
      </p>

      <div aria-live="polite">
        {error && <div className="alert error" role="alert">{error}</div>}
      </div>

      <form onSubmit={handleSubmit} className="form" aria-label="Force password change form">
        {/* Old Password */}
        <label>
          <span>Current Password</span>
          <div className="pw-field">
            <input
              type={showOldPwd ? 'text' : 'password'}
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, old: true }))}
              placeholder="Enter your current password"
              required
              autoComplete="current-password"
              autoFocus
              minLength={8}
            />
            <button 
              type="button" 
              className="btn tiny" 
              onClick={() => setShowOldPwd(v => !v)} 
              aria-pressed={showOldPwd}
              aria-label={showOldPwd ? 'Hide password' : 'Show password'}
            >
              {showOldPwd ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>

        {/* New Password */}
        <label>
          <span>New Password</span>
          <div className="pw-field">
            <input
              type={showNewPwd ? 'text' : 'password'}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, new: true }))}
              placeholder="Create a new password"
              required
              autoComplete="new-password"
              minLength={PASSWORD_RULES.minLength}
            />
            <button 
              type="button" 
              className="btn tiny" 
              onClick={() => setShowNewPwd(v => !v)}
              aria-pressed={showNewPwd}
              aria-label={showNewPwd ? 'Hide password' : 'Show password'}
            >
              {showNewPwd ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {/* Password strength feedback */}
          {touched.new && newPwd && (
            <small 
              style={{ 
                color: passwordStrength.isValid 
                  ? (passwordStrength.strength === 'strong' ? '#106b38' : '#315d86') 
                  : '#c41e3a',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span 
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: passwordStrength.isValid 
                    ? (passwordStrength.strength === 'strong' ? '#106b38' : '#6cc3ff') 
                    : '#c41e3a' 
                }}
              />
              {passwordStrength.message}
            </small>
          )}
          
          {/* Password rules */}
          <small style={{ color: 'var(--muted)', marginTop: '4px' }}>
            Must be at least {PASSWORD_RULES.minLength} characters with a mix of letters and numbers
          </small>
        </label>

        {/* Confirm Password */}
        <label>
          <span>Confirm New Password</span>
          <div className="pw-field">
            <input
              type={showConfirmPwd ? 'text' : 'password'}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, confirm: true }))}
              placeholder="Re-enter your new password"
              required
              autoComplete="new-password"
              minLength={PASSWORD_RULES.minLength}
            />
            <button 
              type="button" 
              className="btn tiny" 
              onClick={() => setShowConfirmPwd(v => !v)}
              aria-pressed={showConfirmPwd}
              aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
            >
              {showConfirmPwd ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {/* Match validation */}
          {showMatchError && (
            <small style={{ color: '#c41e3a', fontWeight: 600 }}>
              Passwords do not match
            </small>
          )}
          {touched.confirm && passwordsMatch && (
            <small style={{ color: '#106b38', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#106b38' }} />
              Passwords match
            </small>
          )}
        </label>

        <div className="actions" style={{ marginTop: '20px' }}>
          <button 
            className="btn primary" 
            type="submit" 
            disabled={loading || !isFormValid}
            style={{ fontSize: '1.05rem', padding: '12px 24px' }}
          >
            {loading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span className="spinner" aria-hidden="true" /> Updating password…
              </span>
            ) : (
              'Save new password'
            )}
          </button>
        </div>

        <div className="below-form" style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            type="button"
            className="link-btn"
            onClick={handleSignOut}
            disabled={loading}
          >
            Sign out instead
          </button>
        </div>
      </form>
    </main>
  );
};
